import { createContext, useContext, type ReactNode } from 'react'

import { TASKITEM_NAME } from '~shared/richtext/nodes/taskitem.node'

import { TASK_ITEM_SELECTOR } from '@/features/richtext/nodes/taskitem/taskitem.utils'
import type { NodeViewStaticProps } from '@/features/richtext/tiptap.helpers'

/**
 * Accessible name for a task item's checkbox. Matches the default label of
 * TipTap's live node view.
 */
function getTaskItemCheckboxLabel(node: NodeViewStaticProps['node']) {
  return `Task item checkbox for ${node.textContent || 'empty task item'}`
}

/**
 * Provided by `RichtextPreviewRenderer` when task items can be toggled.
 * `onToggle` receives the `<li>` of the task item whose checkbox changed.
 * `focusSibling` moves focus to the task item `delta` positions away, in
 * document order, and stays put at the ends.
 * `focusable` puts the checkboxes in tab order.
 * `blocked` stops toggling for now: 'pending' while a save runs, 'error' while
 * a failed save awaits Retry or Abandon. Without a provider, checkboxes are
 * rendered `disabled`.
 */
export const TaskItemToggleContext = createContext<{
  onToggle: (taskItem: HTMLLIElement) => void
  focusSibling: (taskItem: HTMLLIElement, delta: -1 | 1) => void
  focusable: boolean
  blocked: 'pending' | 'error' | null
} | null>(null)

/**
 * Static view of a task item, used by previews, output and prompter.
 *
 * Mirrors the markup from TipTap's `renderHTML()`. The checkbox is enabled and
 * controlled when a toggle handler is provided, and `disabled` otherwise.
 *
 * @remarks
 *   Keyboard reachable only in the selected cell: Tab from the cell reaches the
 *   checkboxes, Space toggles natively, and Up/Down move to the previous or
 *   next task item in the cell. While a save is pending or failed it uses
 *   `aria-disabled` instead of `disabled`, because disabling a focused element
 *   blurs it. Mouse clicks don't move focus (see the label's `onMouseDown`), so
 *   the global Space shortcut keeps working for them.
 */
export function TaskItemNodeViewStatic({
  node,
  children,
}: NodeViewStaticProps & { children?: ReactNode }) {
  const toggle = useContext(TaskItemToggleContext)
  const canToggle = toggle !== null && toggle.blocked === null
  const checked = Boolean(node.attrs.checked)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const item = e.currentTarget.closest<HTMLLIElement>(TASK_ITEM_SELECTOR)
    if (item && canToggle) toggle.onToggle(item)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

    const item = e.currentTarget.closest<HTMLLIElement>(TASK_ITEM_SELECTOR)
    if (!item || !toggle) return

    e.preventDefault() // Don't scroll
    toggle.focusSibling(item, e.key === 'ArrowUp' ? -1 : 1)
  }

  return (
    <li
      data-type={TASKITEM_NAME}
      data-checked={checked}
    >
      <label
        // On the label, not the input: its padding is clickable too. Kept while
        // `disabled`, so clicks during a save don't select or edit the cell.
        // Keep focus in the cell, like the live editor
        onMouseDown={toggle ? (e) => e.preventDefault() : undefined}
        // Keep clicks on the checkbox from selecting or editing the cell
        onClick={toggle ? (e) => e.stopPropagation() : undefined}
        onDoubleClick={toggle ? (e) => e.stopPropagation() : undefined}
      >
        <input
          type="checkbox"
          checked={checked}
          aria-label={getTaskItemCheckboxLabel(node)}
          tabIndex={toggle?.focusable ? 0 : -1}
          disabled={!toggle}
          aria-disabled={toggle?.blocked != null || undefined}
          data-blocked={toggle?.blocked ?? undefined}
          onChange={toggle ? handleChange : undefined}
          onKeyDown={toggle ? handleKeyDown : undefined}
        />
        <span />
      </label>
      <div>{children}</div>
    </li>
  )
}
